import React, { createContext, useContext } from 'react';
import { useAuth as useAuthHook } from '../hooks/useAuth';
import type { AuthState, AuthActions } from '../hooks/useAuth';

/**
 * AuthContext
 *
 * Wraps the existing useAuth hook to provide auth state via React Context,
 * enabling any component in the tree to access auth state without prop drilling.
 *
 * Phase 4 upgrade: This context will read `role` and `orgId` from JWT Custom Claims
 * instead of from the Firestore `users` collection. No consumer changes required.
 *
 * Uses a window-level singleton to guarantee a single context object across
 * all module instances (prevents Vite HMR module duplication).
 */

type AuthContextValue = AuthState & AuthActions;

const noop = async () => {};

const AUTH_DEFAULT: AuthContextValue = {
    user: null,
    userProfile: null,
    loading: true,
    error: null,
    claimsRole: null,
    claimsOrgId: null,
    authResolved: false,
    claimsKnown: false,
    profileSettled: false,
    awaitingEmailVerification: false,
    signIn: noop,
    signUp: noop,
    signInGoogle: noop,
    sendEmailLink: noop,
    logout: noop,
    clearError: () => {},
    refreshClaims: noop,
} as AuthContextValue;

// Singleton: one context object even if Vite evaluates this module twice
const CTX_KEY = '__DEKKO_AUTH_CTX__';
const AuthContext: React.Context<AuthContextValue> =
    (window as any)[CTX_KEY] ||
    ((window as any)[CTX_KEY] = createContext<AuthContextValue>(AUTH_DEFAULT));

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const auth = useAuthHook();

    return (
        <AuthContext.Provider value={auth}>
            {children}
        </AuthContext.Provider>
    );
};

/**
 * useAuthContext - access auth from any component via context.
 *
 * Returns the same shape as the existing useAuth hook, so consumers
 * can be migrated incrementally:
 *   const { user, userProfile, loading, signIn, logout } = useAuthContext();
 */
export const useAuthContext = (): AuthContextValue => {
    return useContext(AuthContext);
};

export default AuthContext;
